import React, { FC, useMemo } from "react";
import clsx from "clsx";
import { useTranslation } from "lib/i18n/use-translation";
import {
  CardVariant,
  CardSize,
  ContentCard,
  CardItemType,
} from "components/ui";
import { extractItem } from "components/ui/ContentCard/utils";
import { CardButtonProps } from "components/ui/ContentCard/ContentCard";
import { PageLoading, PageError } from "../page-container";
import { LearnerRecording } from "../../api/types";
import { ReactComponent as PlayIcon } from "Assets/playIcon.svg";
import { ReactComponent as HeartIcon } from "Assets/icon/heart-icon.svg";
import useFavorite from "../../hooks/use-favourite";
import { trackEvent } from "utility/mixpanel";
import { getTrackingParamsFromUrl } from "../discover/utils";
import styles from "./content-card-grid.module.scss";
import { useOpenModalFromCard } from "providers/content-modal-manager";

interface ContentCardGridProps {
  items: LearnerRecording[];
  listName: string;
  loading?: boolean;
  error?: boolean;
  className?: string;
  hasActionButtons?: boolean;
  variant?: CardVariant;
  size?: CardSize;
}

const ContentCardGrid: FC<ContentCardGridProps> = ({
  items,
  listName,
  loading = false,
  error = false,
  className,
  hasActionButtons = false,
  variant = CardVariant.IMAGE,
  size = CardSize.REGULAR,
}) => {
  const { onFavourite, loading: isFavouriteLoading } = useFavorite();
  const openModalFromCard = useOpenModalFromCard();
  const { t } = useTranslation(["components", "common"]);

  const getActionButtons = (
    item: CardItemType,
    listPosition: number
  ): CardButtonProps[] => {
    if (!hasActionButtons) return [];

    const buttons: CardButtonProps[] = [
      {
        icon: <PlayIcon />,
        ariaLabel: t("common:a11y.watchNow"),
        onClick: (e: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
          e.preventDefault();
          // for some reason I can't figure out, using logPlayClick from useCardTracking causes impressions to render twice. So I've implemented a bespoke solution here.
          const { appliedFilters, searchTerm } = getTrackingParamsFromUrl();
          trackEvent("Recording Play Button Clicked", {
            "Recording ID": itemId,
            "Recording Title": item.title,
            "List Name": listName,
            "List Position": listPosition,
            "Current Page": window.location.href,
            "Applied Filters": appliedFilters,
            "Search Term": searchTerm,
          });
          openModalFromCard({ type: "recording", id: itemId }, true);
        },
      },
    ];

    // Only add favorite button for items that have the isFavourite property
    if (item.isFavourite !== undefined) {
      const favouriteAction = item.isFavourite
        ? t("common:a11y.removeFromFavourites")
        : t("common:a11y.addToFavourites");
      // Analytics verb stays English so the event name doesn't fragment by locale.
      const trackingAction = item.isFavourite
        ? "Removed from favourites"
        : "Added to Favourites";

      buttons.push({
        icon: (
          <HeartIcon
            stroke={
              item.isFavourite ? styles["love-red"] : styles["dark-shade-grey"]
            }
            color={item.isFavourite ? styles["love-red"] : "transparent"}
          />
        ),
        ariaLabel: favouriteAction,
        loading: isFavouriteLoading,
        onClick: (e: React.MouseEvent<HTMLButtonElement>, itemId: string) => {
          e.preventDefault();
          const { appliedFilters, searchTerm } = getTrackingParamsFromUrl();
          trackEvent(`Recording ${trackingAction}`, {
            "Recording ID": itemId,
            "Recording Title": item.title,
            "List Name": listName,
            "List Position": listPosition,
            "Current Page": window.location.href,
            "Applied Filters": appliedFilters,
            "Search Term": searchTerm,
          });
          onFavourite({
            id: itemId,
            isFavourite: item.isFavourite || false,
            __typename: item.__typename as any,
          });
        },
      });
    }

    return buttons;
  };

  const cardItems = useMemo(() => {
    return items.map(extractItem);
  }, [items]);

  if (loading) {
    return <PageLoading />;
  }

  if (error) {
    return <PageError />;
  }

  return (
    <div className={clsx(styles["content-card-grid"], className)}>
      {cardItems.length === 0 && (
        <div className={styles["no-results"]}>
          <h2>{t("components:contentLibrary.emptyTitle")}</h2>
          <p>{t("components:contentLibrary.emptySubtitle")}</p>
        </div>
      )}

      {cardItems.length > 0 && (
        <div className={clsx(styles.grid, className)}>
          {cardItems.map((item, index) => (
            <ContentCard
              key={item.id}
              item={item}
              variant={variant}
              size={size}
              listName={listName}
              listPosition={index + 1}
              actionButtons={getActionButtons(item, index + 1)}
              widthMode="full"
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ContentCardGrid;
